import React from "react";
import { motion } from "framer-motion";
import { Play, BedDouble, Bath, Users } from "lucide-react";

const Stat = ({ icon: Icon, value, label }) => (
  <div className="flex items-center gap-3">
    <Icon className="w-5 h-5 text-[#D6B15E]" strokeWidth={1.5} />
    <div>
      <p className="text-lg font-light leading-none">{value}</p>
      <p className="text-[11px] tracking-widest uppercase text-[#A9B8A6] mt-1">{label}</p>
    </div>
  </div>
);

const TourGrid = ({ property, tours, onOpenTour }) => {
  return (
    <section id="tours" className="max-w-6xl mx-auto px-6 sm:px-10 py-24" data-testid="tours-section">
      {/* Property intro */}
      <div className="grid lg:grid-cols-2 gap-12 items-start mb-20">
        <div>
          <p className="text-xs tracking-[0.25em] uppercase text-[#D6B15E] mb-4">The Residence</p>
          <h2 className="font-heading font-light text-4xl sm:text-5xl tracking-tight leading-tight">
            Walk through every room before you book
          </h2>
        </div>
        <div className="lg:pt-8">
          <p className="text-[#A9B8A6] font-light leading-relaxed text-lg">{property?.description}</p>
          <div className="flex flex-wrap gap-8 mt-8">
            <Stat icon={BedDouble} value={property?.bedrooms} label="Bedrooms" />
            <Stat icon={Bath} value={property?.bathrooms} label="Bathrooms" />
            <Stat icon={Users} value={property?.guests} label="Guests" />
          </div>
          {property?.highlights?.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-8">
              {property.highlights.map((h) => (
                <span key={h} className="text-xs tracking-wide border border-white/15 rounded-full px-4 py-2 text-[#A9B8A6]">
                  {h}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Tour cards */}
      <p className="text-xs tracking-[0.25em] uppercase text-[#D6B15E] mb-8">Immersive Tours</p>
      <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
        {tours.map((tour, i) => {
          const large = tour.featured || tours.length === 1;
          return (
            <motion.button
              key={tour.id}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: i * 0.1 }}
              onClick={() => onOpenTour(tour)}
              data-testid={`tour-card-${tour.id}`}
              className={`group relative overflow-hidden rounded-2xl border border-white/10 text-left ${
                large ? "md:col-span-12 aspect-[16/9]" : "md:col-span-6 aspect-[4/3]"
              }`}
            >
              <img
                src={tour.thumbnail_url}
                alt={tour.title}
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/20 to-transparent" />

              <div className="absolute top-5 left-5 flex items-center gap-2 backdrop-blur-md bg-black/40 border border-white/10 rounded-full px-3 py-1.5">
                <span className="w-2 h-2 rounded-full bg-[#D6B15E]" />
                <span className="text-[10px] tracking-[0.2em] uppercase">
                  {tour.media_type === "pano_photo" || tour.media_type === "pano_video"
                    ? "360°"
                    : tour.media_type === "pano_wide"
                    ? "Panorama"
                    : tour.media_type === "photo"
                    ? "Photo"
                    : "Video"}
                </span>
              </div>

              <div className="absolute bottom-0 left-0 right-0 p-6 flex items-end justify-between">
                <div>
                  <p className="text-[11px] tracking-[0.2em] uppercase text-[#D6B15E]">{tour.room}</p>
                  <h3 className="font-heading text-2xl sm:text-3xl font-light mt-1">{tour.title}</h3>
                </div>
                <div className="w-14 h-14 shrink-0 rounded-full bg-white text-black flex items-center justify-center transition-colors duration-300 group-hover:bg-[#D6B15E]">
                  <Play className="w-5 h-5 ml-0.5" strokeWidth={1.5} />
                </div>
              </div>
            </motion.button>
          );
        })}
      </div>
    </section>
  );
};

export default TourGrid;
